import React, { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";

const BASE = "https://dailydish.in/api/admin";

const thStyle = {
  padding: "14px 16px",
  textAlign: "left",
  fontWeight: "700",
  color: "#3a5a0e",
  fontSize: "12px",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
};

const tdStyle = { padding: "12px 16px", whiteSpace: "nowrap" };

const HubVisitTrackerAdmin = () => {
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedHub, setExpandedHub] = useState(null);
  const [visitLogs, setVisitLogs] = useState({}); // hubKey -> array of visit rows
  const [logLoading, setLogLoading] = useState(null); // hubKey currently loading

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE}/hub-visits/stats`, {
        params: { date },
      });
      if (res.data.success) setHubs(res.data.hubs);
    } catch (err) {
      console.error(err);
      setHubs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    setVisitLogs({});
    setExpandedHub(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const toggleHub = async (hubId) => {
    const key = hubId || "unknown";
    if (expandedHub === key) {
      setExpandedHub(null);
      return;
    }
    setExpandedHub(key);
    if (visitLogs[key]) return; // already fetched for this date

    try {
      setLogLoading(key);
      const res = await axios.get(`${BASE}/hub-visits/log`, {
        params: { date, hubId: hubId || "unknown" },
      });
      if (res.data.success) {
        setVisitLogs((prev) => ({ ...prev, [key]: res.data.visits }));
      }
    } catch (err) {
      console.error(err);
      setVisitLogs((prev) => ({ ...prev, [key]: [] }));
    } finally {
      setLogLoading(null);
    }
  };

  const totals = hubs.reduce(
    (acc, h) => ({
      registered: acc.registered + h.registered,
      loggedInVisits: acc.loggedInVisits + h.loggedInVisits,
      anonymousVisits: acc.anonymousVisits + h.anonymousVisits,
      totalVisits: acc.totalVisits + h.totalVisits,
    }),
    { registered: 0, loggedInVisits: 0, anonymousVisits: 0, totalVisits: 0 },
  );

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Inter, sans-serif",
        background: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          background: "linear-gradient(135deg, #6B8E23 0%, #4a6e12 100%)",
          borderRadius: "16px",
          padding: "24px 28px",
          color: "#fff",
          marginBottom: "24px",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "700" }}>
          🌐 Hub Visit Tracker
        </h2>
        <p style={{ margin: "4px 0 0", opacity: 0.85, fontSize: "14px" }}>
          Website visits per hub, logged-in vs anonymous, hour by hour (IST)
        </p>
      </div>

      <div
        style={{
          display: "flex",
          gap: "12px",
          flexWrap: "wrap",
          alignItems: "flex-end",
          marginBottom: "20px",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "12px",
              color: "#666",
              fontWeight: "600",
              marginBottom: "6px",
            }}
          >
            Date
          </div>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{
              padding: "10px 14px",
              borderRadius: "10px",
              border: "1.5px solid #ddd",
            }}
          />
        </div>
        <button
          onClick={fetchStats}
          style={{
            padding: "10px 20px",
            borderRadius: "10px",
            border: "none",
            background: "#6B8E23",
            color: "#fff",
            fontWeight: "700",
            cursor: "pointer",
          }}
        >
          Refresh
        </button>
      </div>

      <div
        style={{
          background: "#fff",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          border: "1px solid #e8f0d8",
        }}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px", color: "#888" }}>
            Loading...
          </div>
        ) : hubs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px", color: "#888" }}>
            No visits recorded for this date.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "13px",
              }}
            >
              <thead>
                <tr
                  style={{
                    background:
                      "linear-gradient(135deg, #f6faf0 0%, #edf5e1 100%)",
                    borderBottom: "2px solid #d4e8b0",
                  }}
                >
                  {[
                    "Hub",
                    "Registered",
                    "Total Visits",
                    "Logged-in",
                    "Anonymous",
                    "",
                  ].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {hubs.map((h, idx) => {
                  const key = h.hubId || "unknown";
                  const isExpanded = expandedHub === key;
                  return (
                    <React.Fragment key={key}>
                      <tr
                        onClick={() => toggleHub(h.hubId)}
                        style={{
                          borderBottom: "1px solid #f0f5e8",
                          background: idx % 2 === 0 ? "#fff" : "#fafcf7",
                          cursor: "pointer",
                        }}
                      >
                        <td
                          style={{
                            ...tdStyle,
                            fontWeight: "600",
                            color: "#2c2c2c",
                          }}
                        >
                          {h.hubName || "Unknown / No Hub Yet"}
                        </td>
                        <td style={tdStyle}>{h.registered}</td>
                        <td style={tdStyle}>{h.totalVisits}</td>
                        <td style={tdStyle}>{h.loggedInVisits}</td>
                        <td style={tdStyle}>{h.anonymousVisits}</td>
                        <td
                          style={{
                            ...tdStyle,
                            color: "#6B8E23",
                            fontWeight: "700",
                          }}
                        >
                          {isExpanded ? "▲ Hide detail" : "▼ Exact times"}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td
                            colSpan={6}
                            style={{
                              padding: "0 16px 16px",
                              background: "#fafcf7",
                            }}
                          >
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                fontSize: "12px",
                                marginTop: "8px",
                              }}
                            >
                              <thead>
                                <tr
                                  style={{ borderBottom: "1px solid #e0e8d0" }}
                                >
                                  {[
                                    "Hour (IST)",
                                    "Logged-in",
                                    "Anonymous",
                                    "Total",
                                  ].map((h2) => (
                                    <th
                                      key={h2}
                                      style={{
                                        ...thStyle,
                                        padding: "8px 12px",
                                        fontSize: "11px",
                                      }}
                                    >
                                      {h2}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {h.hourly
                                  .filter((row) => row.totalVisits > 0)
                                  .map((row) => (
                                    <tr
                                      key={row.hour}
                                      style={{
                                        borderBottom: "1px solid #f0f5e8",
                                      }}
                                    >
                                      <td
                                        style={{
                                          ...tdStyle,
                                          padding: "8px 12px",
                                        }}
                                      >
                                        {String(row.hour).padStart(2, "0")}:00
                                      </td>
                                      <td
                                        style={{
                                          ...tdStyle,
                                          padding: "8px 12px",
                                        }}
                                      >
                                        {row.loggedInVisits}
                                      </td>
                                      <td
                                        style={{
                                          ...tdStyle,
                                          padding: "8px 12px",
                                        }}
                                      >
                                        {row.anonymousVisits}
                                      </td>
                                      <td
                                        style={{
                                          ...tdStyle,
                                          padding: "8px 12px",
                                        }}
                                      >
                                        {row.totalVisits}
                                      </td>
                                    </tr>
                                  ))}
                                {h.hourly.every(
                                  (row) => row.totalVisits === 0,
                                ) && (
                                  <tr>
                                    <td
                                      colSpan={4}
                                      style={{ padding: "12px", color: "#888" }}
                                    >
                                      No visits this hour.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>

                            <div
                              style={{
                                margin: "16px 0 8px",
                                fontSize: "12px",
                                fontWeight: "700",
                                color: "#3a5a0e",
                                textTransform: "uppercase",
                              }}
                            >
                              Exact Visit Log
                            </div>
                            {logLoading === key ? (
                              <div style={{ padding: "12px", color: "#888" }}>
                                Loading...
                              </div>
                            ) : (
                              <div
                                style={{
                                  maxHeight: "320px",
                                  overflowY: "auto",
                                }}
                              >
                                <table
                                  style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontSize: "12px",
                                  }}
                                >
                                  <thead>
                                    <tr
                                      style={{
                                        borderBottom: "1px solid #e0e8d0",
                                        position: "sticky",
                                        top: 0,
                                        background: "#fafcf7",
                                      }}
                                    >
                                      {[
                                        "Time (IST)",
                                        "Visitor",
                                        "Entry Page",
                                        "Ordered",
                                        "Registered",
                                        "Lifetime Orders",
                                        "Last Order (IST)",
                                        "Total Visit Days",
                                        "Visits Today",
                                        "Drop Point",
                                      ].map((h3) => (
                                        <th
                                          key={h3}
                                          style={{
                                            ...thStyle,
                                            padding: "8px 12px",
                                            fontSize: "11px",
                                          }}
                                        >
                                          {h3}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(visitLogs[key] || []).map((v, i) => (
                                      <tr
                                        key={`${v.sessionId}_${i}`}
                                        style={{
                                          borderBottom: "1px solid #f0f5e8",
                                        }}
                                      >
                                        <td
                                          style={{
                                            ...tdStyle,
                                            padding: "8px 12px",
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          {v.time}
                                        </td>
                                        <td
                                          style={{
                                            ...tdStyle,
                                            padding: "8px 12px",
                                          }}
                                        >
                                          {v.loggedIn
                                            ? `${v.visitorName || "—"}${v.visitorMobile ? ` (${v.visitorMobile})` : ""}`
                                            : "Anonymous"}
                                        </td>
                                        <td
                                          style={{
                                            ...tdStyle,
                                            padding: "8px 12px",
                                          }}
                                        >
                                          {v.path || "/"}
                                        </td>
                                        <td
                                          style={{
                                            ...tdStyle,
                                            padding: "8px 12px",
                                            color: v.ordered
                                              ? "#3a5a0e"
                                              : "#b3401f",
                                            fontWeight: "700",
                                          }}
                                        >
                                          {v.ordered
                                            ? `✅ ${v.firstOrderTime}${v.orderCount > 1 ? ` (+${v.orderCount - 1} more)` : ""}`
                                            : "—"}
                                        </td>
                                        <td
                                          style={{
                                            ...tdStyle,
                                            padding: "8px 12px",
                                          }}
                                        >
                                          {v.registered || "—"}
                                        </td>
                                        <td
                                          style={{
                                            ...tdStyle,
                                            padding: "8px 12px",
                                          }}
                                        >
                                          {v.loggedIn ? v.totalOrders : "—"}
                                        </td>
                                        <td
                                          style={{
                                            ...tdStyle,
                                            padding: "8px 12px",
                                          }}
                                        >
                                          {v.lastOrder || "—"}
                                        </td>
                                        <td
                                          style={{
                                            ...tdStyle,
                                            padding: "8px 12px",
                                          }}
                                        >
                                          {v.loggedIn ? v.totalVisitDays : "—"}
                                        </td>
                                        <td
                                          style={{
                                            ...tdStyle,
                                            padding: "8px 12px",
                                          }}
                                        >
                                          {v.todayVisits}
                                        </td>
                                        <td
                                          style={{
                                            ...tdStyle,
                                            padding: "8px 12px",
                                          }}
                                        >
                                          {v.dropPoint || "/"}
                                        </td>
                                      </tr>
                                    ))}
                                    {visitLogs[key] &&
                                      visitLogs[key].length === 0 && (
                                        <tr>
                                          <td
                                            colSpan={10}
                                            style={{
                                              padding: "12px",
                                              color: "#888",
                                            }}
                                          >
                                            No visits recorded.
                                          </td>
                                        </tr>
                                      )}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr
                  style={{ borderTop: "2px solid #d4e8b0", fontWeight: "700" }}
                >
                  <td style={tdStyle}>Total</td>
                  <td style={tdStyle}>{totals.registered}</td>
                  <td style={tdStyle}>{totals.totalVisits}</td>
                  <td style={tdStyle}>{totals.loggedInVisits}</td>
                  <td style={tdStyle}>{totals.anonymousVisits}</td>
                  <td style={tdStyle}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default HubVisitTrackerAdmin;
